import { Stack } from '@servicetitan/design-system';
import CustomerDetails from '../CustomerDetails';
import CustomerMfeAdapter from '../CustomerMfeAdapter';
import styles from './TaskView.module.css';
import TaskCustomerSearch from '@components/CustomerSearch/TaskCustomerSearch';

import useTasks from '@modules/TasksModule/hooks/useTasks';
import TasksModule from '@modules/TasksModule';
import classNames from 'classnames';
import { useCallback } from 'react';
import TaskLoadingOverlay from '@app/layouts/MainLayout/components/TaskLoadingOverlay/TaskLoadingOverlay';
import { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import useFeatureFlag from '@modules/FeatureFlagModule/hooks/useFeatureFlag/useFeatureFlag';
import useBookingRetentionEnabled from '@modules/InboxModule/hooks/useBookingRetentionEnabled';

type Props = {
  taskId: string;
  taskTenantId: string;
  /** Tenant-level OrgNode Guid for taskTenantId. May be absent if the task's tenant isn't in the user's tenant list. */
  taskOrgNodeId?: string;
  show?: boolean;
};

/**
 * This component is used to display the customer search and customer details for a task that has a tenantId
 *
 * @param taskId
 * @param taskTenantId - This is to explicitly pass the tenantId of the task. As this component should not be used without a tenantId.
 * @param taskOrgNodeId - The tenant's OrgNode Guid, resolved by the caller — feeds customer-mfe, which scopes by Guid rather than numeric tenantId.
 * @param show - Whether to show the component. Used to show/hide the component and preserve state.
 */
const TaskView = ({
  taskId,
  show = false,
  taskTenantId,
  taskOrgNodeId,
}: Props) => {
  const { taskById } = useTasks();
  const task = taskById[taskId];
  const isCustomerMfeEnabled = useFeatureFlag(
    FeatureFlagKeys.featureEnableAgentInboxForCalls,
    { defaultValue: false },
  );

  const isRetentionEnabled = useBookingRetentionEnabled();

  const handleBackClick = useCallback(() => {
    TasksModule.setCustomerOnTask(taskId, undefined);
  }, [taskId]);

  // if the task is not found, in general we should have a task.
  if (!task) {
    return <div>Task not found</div>;
  }

  // Retention on: seed the MFE from the customer the CSR picked, org node and
  // customer together, so a view switched to after a pick in the tenant-less
  // view (or rebuilt after the cap) never pairs the call's tenant with a
  // customer from another tenant. No pick org node means no customer to seed.
  const pickOrgNodeId: string | undefined = isRetentionEnabled
    ? task.attributes.customerOrgNodeId
    : undefined;
  const seedOrgNodeId = pickOrgNodeId ?? taskOrgNodeId;
  const hasPickedCustomer =
    Boolean(pickOrgNodeId) && Boolean(task.attributes.customerId);
  // Retention off seeds the task customer as master does.
  const seedCustomerId =
    isRetentionEnabled && !hasPickedCustomer
      ? undefined
      : task.attributes.customerId;
  // Campaign attribution belongs to the call's tenant.
  const campaignId =
    !isRetentionEnabled || seedOrgNodeId === taskOrgNodeId
      ? task.attributes.campaign?.id
      : undefined;
  // A cross-tenant pick shows under its own tenant, not the call's.
  const customerDetailsTenantId = isRetentionEnabled
    ? task.attributes.customerTenantId ?? taskTenantId
    : taskTenantId;

  // we are using 'show' here to toggle the 'display' of the component
  // so it doesn't mount/unmount every time it's selected, because it
  // will lose any state
  return (
    <Stack
      className={classNames('h-100', 'flex-grow-1', {
        'd-b': show,
        [styles.TaskViewShown]: show,
        [styles.TaskViewHidden]: !show,
      })}
    >
      <TaskLoadingOverlay taskId={taskId}>
        <div
          className={classNames('bg-neutral-40', 'h-100', 'of-y-auto', 'd-f', {
            // customer-mfe owns its own internal spacing and should fill this
            // pane edge to edge, unlike the native search/details forms.
            'p-x-3': !isCustomerMfeEnabled,
            'p-y-2': !isCustomerMfeEnabled,
          })}
        >
          {isCustomerMfeEnabled ? (
            <CustomerMfeAdapter
              orgNodeId={seedOrgNodeId}
              customerId={seedCustomerId}
              // task.from unformatted, not maskPhoneNumber(task.from): the MFE
              // forwards this to the agent-inbox BFF as a free-text search, where
              // a display-masked "(717) 201-2010" fans out to far more matches
              // than the E.164 form and so never narrows to the single match the
              // panel needs to auto-select (CCPE-1656 Defect A). The adapter drops
              // blank caller IDs, so this passes through unguarded.
              phoneNumber={task.from}
              campaignId={campaignId}
              callSid={task.attributes.sessionSid}
              guardNavigation={show}
              viewKey={task.viewKey}
              onCustomerChanged={(customer) =>
                TasksModule.setCustomerOnTask(taskId, customer)
              }
            />
          ) : (
            <>
              {task.attributes.customerId && (
                <CustomerDetails
                  customerId={task.attributes.customerId}
                  tenantId={customerDetailsTenantId}
                  onBackClick={handleBackClick}
                  displayName={task.displayName}
                />
              )}
              <TaskCustomerSearch
                taskId={taskId}
                taskTenantId={taskTenantId}
                show={!task.attributes.customerId}
              />
            </>
          )}
        </div>
      </TaskLoadingOverlay>
    </Stack>
  );
};

export default TaskView;
